/**
 * /app/compras — layout compartido con tabs de ruta.
 * Evita que Órdenes/Lotes pierdan la navegación de sección.
 */
import { InlineSupplierAi } from "@/components/renata/InlineSupplierAi";
import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader } from "@/components/ds";
import { cn } from "@/lib/utils";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

type ComprasTabId = "recepciones" | "alertas" | "ordenes" | "lotes";

function activeTabId(pathname: string, tabSearch?: string): ComprasTabId {
  if (pathname.includes("/compras/ordenes")) return "ordenes";
  if (pathname.includes("/compras/lotes")) return "lotes";
  if (tabSearch === "alertas") return "alertas";
  return "recepciones";
}

function ComprasLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const search = useRouterState({
    select: (s) => s.location.search as { tab?: string },
  });
  const active = activeTabId(pathname, search.tab);

  const subtitle =
    active === "ordenes"
      ? "Pedidos a proveedores — creación y seguimiento"
      : active === "lotes"
        ? "Controlá fechas de vencimiento. Se usa primero lo que vence antes."
        : active === "alertas"
          ? "Alertas de stock, vencimientos y pedidos a proveedores"
          : "Registrá la mercadería que entra. Suma stock a tus insumos o productos.";

  return (
    <div>
      <div className="mb-3"><InlineSupplierAi entity="proveedor" /></div>
        <MerchantExperienceBar screenId="compras" />
      <CrudShellHeader
        title="Compras"
        description={subtitle}
        backHref="/app/operaciones"
        backLabel="Volver a Abastecimiento"
      />
      <ClinicalPageShell offset="none">
        <nav
          aria-label="Secciones de compras"
          className="mb-4 flex gap-1 overflow-x-auto border-b border-border/40"
        >
          <Link
            to="/app/compras"
            search={{ tab: "recepciones", ingredientId: undefined } as never}
            className={cn(
              "flex-shrink-0 border-b-2 px-3 py-2 text-sm transition-colors",
              active === "recepciones"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            aria-current={active === "recepciones" ? "page" : undefined}
          >
            Recepciones
          </Link>
          <Link
            to="/app/compras"
            search={{ tab: "alertas", ingredientId: undefined } as never}
            className={cn(
              "flex-shrink-0 border-b-2 px-3 py-2 text-sm transition-colors",
              active === "alertas"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            aria-current={active === "alertas" ? "page" : undefined}
          >
            Alertas
          </Link>
          <Link
            to="/app/compras/ordenes"
            search={{} as never}
            className={cn(
              "flex-shrink-0 border-b-2 px-3 py-2 text-sm transition-colors",
              active === "ordenes"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            aria-current={active === "ordenes" ? "page" : undefined}
          >
            Órdenes de compra
          </Link>
          <Link
            to="/app/compras/lotes"
            search={{} as never}
            className={cn(
              "flex-shrink-0 border-b-2 px-3 py-2 text-sm transition-colors",
              active === "lotes"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            aria-current={active === "lotes" ? "page" : undefined}
          >
            Lotes y vencimientos
          </Link>
        </nav>
        <Outlet />
      </ClinicalPageShell>
    </div>
  );
}

export const Route = createFileRoute("/app/compras")({
  component: ComprasLayout,
  validateSearch: (s: Record<string, unknown>) => ({
    tab:
      s.tab === "alertas" || s.tab === "recepciones"
        ? (s.tab as "alertas" | "recepciones")
        : undefined,
  }),
});
